import { formatDate } from 'src/modules/shared/utils/formatting/date.util';

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_ONLY_UTC_MIDNIGHT_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/;

/**
 * Compatibility helpers for DATE custom-field values written before the
 * `includeTime` rollout standardized frontend writes.
 *
 * Why this exists:
 * some legacy flows stored date-only values as `YYYY-MM-DD`, while other flows
 * stored the same calendar day as a UTC-midnight ISO string. If we parse those
 * rows naively after enabling `includeTime`, users west of UTC can see the
 * previous day/time when editing or rendering historical values.
 *
 * When this can be removed:
 * after we are confident the database no longer contains legacy date-only rows
 * for custom fields, or after we run a migration/backfill that rewrites every
 * legacy DATE value into the canonical representation expected by the current
 * UI. Until then, these adapters preserve historical calendar-day semantics.
 */

interface LegacyDateOnlyParts {
  /** Four-digit calendar year extracted from the stored legacy value. */
  year: string;
  /** Two-digit calendar month extracted from the stored legacy value. */
  month: string;
  /** Two-digit calendar day extracted from the stored legacy value. */
  day: string;
}

const getRawDateValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || value instanceof Date) {
    return String(value);
  }

  return null;
};

/**
 * Detects legacy date-only DATE custom-field rows.
 *
 * Supported legacy formats:
 * - `YYYY-MM-DD`
 * - `YYYY-MM-DDT00:00:00.000Z`
 */
const getLegacyDateOnlyParts = (
  value: string,
  allowUtcMidnightIso: boolean
): LegacyDateOnlyParts | null => {
  const directMatch = DATE_ONLY_REGEX.exec(value);
  if (directMatch) {
    const [, year, month, day] = directMatch;
    return { year, month, day };
  }

  if (!allowUtcMidnightIso) {
    return null;
  }

  const utcMidnightMatch = DATE_ONLY_UTC_MIDNIGHT_REGEX.exec(value);
  if (utcMidnightMatch) {
    const [, year, month, day] = utcMidnightMatch;
    return { year, month, day };
  }

  return null;
};

/**
 * Formats DATE values for native `date` / `datetime-local` inputs while keeping
 * legacy date-only rows pinned to their original calendar day.
 */
export function formatLegacyAwareCustomFieldDateInputValue(
  value: unknown,
  includeTime: boolean
): string {
  if (!value) {
    return '';
  }

  const rawValue = getRawDateValue(value);
  if (rawValue === null) {
    return '';
  }
  const legacyParts = getLegacyDateOnlyParts(rawValue, !includeTime);
  if (legacyParts) {
    const { year, month, day } = legacyParts;
    return includeTime
      ? `${year}-${month}-${day}T00:00`
      : `${year}-${month}-${day}`;
  }

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (!includeTime) {
    return `${year}-${month}-${day}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Serializes native `date` / `datetime-local` input values to the backend ISO
 * format while keeping date-only selections anchored to local midnight.
 */
export function parseLegacyAwareCustomFieldDateInputValue(
  value: string,
  includeTime: boolean
): string | null {
  if (!value) {
    return null;
  }

  if (includeTime) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  const match = DATE_ONLY_REGEX.exec(value);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const localMidnight = new Date(Number(year), Number(month) - 1, Number(day));

  return Number.isNaN(localMidnight.getTime())
    ? null
    : localMidnight.toISOString();
}

/**
 * Formats DATE values for table/detail display while preserving the historical
 * calendar day of legacy date-only rows.
 */
export function formatLegacyAwareCustomFieldDateDisplayValue(
  value: unknown,
  includeTime: boolean
): string {
  if (!value) {
    return '';
  }

  const rawValue = getRawDateValue(value);
  if (rawValue === null) {
    return '';
  }
  const legacyParts = getLegacyDateOnlyParts(rawValue, !includeTime);
  if (legacyParts) {
    const { year, month, day } = legacyParts;
    const formattedDate = `${day}-${month}-${year}`;
    return includeTime ? `${formattedDate} 00:00` : formattedDate;
  }

  return includeTime
    ? formatDate(rawValue)
    : formatDate(rawValue, 'DD-MM-YYYY');
}
